// زمان و مکانِ نهاییِ یک سیگنال، موقعِ «تأیید نهایی».
//
// منطقِ خالص و بی‌ایمپورتِ پلتفرم، چون هر دو اپ همین تصمیم را می‌گیرند و دو
// بار نوشتنش یعنی دو رفتارِ کمی متفاوت — دقیقاً همان چیزی که این فایل برای
// درست کردنش نوشته شد.
//
// ═══ چه چیزی خراب بود ═══
//
// هر دو اپ زمانِ نهایی را **بدونِ پرسیدن** روی «۲۴ ساعت بعد از الان»
// می‌گذاشتند (`apps/web/.../SignalPage.tsx` و `apps/mobile/app/signal/[id].tsx`).
// یعنی سیگنالِ «شنبه ۸ شب» که پنجشنبه ساعت ۳ قطعی می‌شد، برنامه‌ای برای
// جمعه ساعت ۳ ثبت می‌کرد. نه با زمانِ خودِ سیگنال جور بود نه با هیچ انتخابی.
//
// ═══ قاعده ═══
//
// پیش‌فرض = زمانِ خودِ سیگنال، اگر داشته باشد. اگر نداشته باشد — یعنی
// `approx`/`coordinate`/`from_availability` که اصلاً `starts_at` ندارند —
// خالی می‌ماند و **کاربر باید پرش کند**: «تأیید نهایی» بدونِ زمانِ نهایی
// همان حفره‌ای است که این فایل دارد می‌بندد.
//
// ساعت اختیاری است، مثلِ خودِ فرمِ ساخت: «جمعه، ساعتش را بعداً می‌گوییم»
// یک جوابِ درست است. تاریخ اجباری است.
//
// ═══ نسبتش با dateChoice.ts ═══
//
// این فایل قاعده‌ی تازه‌ای نمی‌سازد؛ فقط پلِ رشته‌هایِ `<input type="date">`
// وب است به `DateChoice`. خودِ تبدیل — از جمله «بی‌ساعت یعنی ظهرِ محلی» —
// همان‌جا می‌ماند، وگرنه دو قرارداد می‌شود و همان تفاوتِ کوچکی پیش می‌آید که
// این‌ها برای جلوگیری‌اش نوشته شده‌اند. موبایل مستقیم با `DateChoice` کار
// می‌کند و اصلاً از این فایل رد نمی‌شود.

import { choiceFrom, startInstant } from './dateChoice.ts';

/** تاریخ و ساعتِ جدا، همان شکلی که فرمِ وب نگه می‌دارد. */
export interface FinalWhen {
  /** `yyyy-mm-dd` — خالی یعنی هنوز انتخاب نشده. */
  date: string;
  /** `hh:mm` — خالی یعنی «فقط روز، بی‌ساعت». */
  time: string;
}

const two = (n: number): string => String(n).padStart(2, '0');

/** `Date` محلی → `yyyy-mm-dd`. عمداً محلی، نه UTC: روزِ کاربر مهم است. */
export const toDateInput = (d: Date): string =>
  `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;

/** `Date` محلی → `hh:mm`. */
export const toTimeInput = (d: Date): string => `${two(d.getHours())}:${two(d.getMinutes())}`;

/**
 * پیش‌فرضِ دیالوگِ تأیید.
 *
 * `dateOnly` را ساعت‌دار نشان نمی‌دهیم: `starts_at` آن سیگنال‌ها عمداً ظهرِ
 * محلی است تا جابه‌جاییِ منطقه‌ی زمانی روز را عوض نکند (نگاه کن به
 * `dateChoice.ts`)، و نشان دادنِ «۱۲:۰۰» به کاربر یعنی یک ساعتِ ساختگی که
 * هیچ‌کس انتخابش نکرده.
 */
export function defaultFinalWhen(
  signal: { startsAt?: string | null; dateOnly?: boolean | null },
): FinalWhen {
  // `starts_at` خرابِ ذخیره‌شده نباید «Invalid Date» را به فرم بریزد.
  if (signal.startsAt && Number.isNaN(new Date(signal.startsAt).getTime())) {
    return { date: '', time: '' };
  }
  const c = choiceFrom(signal);
  if (!c.start) return { date: '', time: '' };
  return {
    date: toDateInput(c.start),
    time: c.time ? `${two(c.time.h)}:${two(c.time.m)}` : '',
  };
}

/**
 * تاریخ و ساعت → یک لحظه‌ی واقعی، از راهِ `startInstant` تا قاعده‌ی
 * «بی‌ساعت یعنی ظهرِ محلی» فقط یک‌جا نوشته شده باشد.
 *
 * `null` یعنی ورودی معتبر نیست و **نباید** چیزی ثبت شود.
 */
export function composeFinalStart(when: FinalWhen): Date | null {
  if (!when.date) return null;
  const [y, m, d] = when.date.split('-').map(Number);
  if (!y || !m || !d) return null;
  let time = null as { h: number; m: number } | null;
  if (when.time) {
    const [hh, mm] = when.time.split(':').map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    time = { h: hh, m: mm };
  }
  return startInstant({ start: new Date(y, m - 1, d), end: null, time });
}

/**
 * چه چیزی برای قطعی کردن کم است — `null` یعنی هیچی، آماده است.
 *
 * ═══ چرا هر سه اجباری‌اند ═══
 *
 * در فرمِ **ساخت** ساعت اختیاری است و درست هم هست: «جمعه، ساعتش را بعداً
 * می‌گوییم» یک دعوتِ کاملاً معتبر است. ولی «تأیید نهایی» حرفِ دیگری می‌زند:
 * یعنی همه بدانند کِی و کجا باشند. یک برنامه‌ی «قطعی»ِ بی‌ساعت و بی‌مکان،
 * قطعی نیست.
 *
 * و دو چیزِ پایین‌دستی مستقیم به این تکیه دارند:
 *   • یادآور — «یک ساعت قبل» بدونِ ساعت هیچ معنایی ندارد.
 *   • فایلِ تقویم — بی‌ساعت یک رویدادِ مبهمِ تمام‌روز می‌شود، و بی‌مکان به
 *     گیرنده نمی‌گوید کجا برود.
 *
 * ترتیبِ برگشت عمدی است: تاریخ، بعد ساعت، بعد مکان — همان ترتیبی که در
 * فرم می‌بینی، تا راهنما به اولین چیزی اشاره کند که چشمت به آن می‌رسد.
 */
export function confirmMissing(
  c: { hasDay: boolean; hasClock: boolean; place: string },
): 'day' | 'clock' | 'place' | null {
  if (!c.hasDay) return 'day';
  if (!c.hasClock) return 'clock';
  if (!c.place.trim()) return 'place';
  return null;
}

/**
 * **همه‌ی** چیزهای کم، نه فقط اولی.
 *
 * چرا هر دو تابع: دکمه‌ی تأیید دیگر خاکستری نمی‌شود — فعال می‌مانَد و با
 * زدنش می‌گوید چه چیزی مانده. دکمه‌ی خاکستری علتش را نمی‌گوید و کاربر را
 * وامی‌دارد حدس بزند کدام فیلد ناقص است؛ خواسته‌ی صریح همین بود.
 *
 * و چون حالا پیام است نه رنگ، باید **کاملِ** فهرست را بگوید: «تاریخ را
 * انتخاب کن» و بعد از پر کردنش «ساعت را هم مشخص کن»، یعنی سه بار
 * رفت‌وبرگشت. یک بار همه را می‌گوید.
 */
export function confirmMissingAll(
  c: { hasDay: boolean; hasClock: boolean; place: string },
): Array<'day' | 'clock' | 'place'> {
  const out: Array<'day' | 'clock' | 'place'> = [];
  if (!c.hasDay) out.push('day');
  if (!c.hasClock) out.push('clock');
  if (!c.place.trim()) out.push('place');
  return out;
}

/** همان بررسی، برای فرمِ رشته‌ایِ وب. */
export const canConfirm = (when: FinalWhen, place: string): boolean =>
  composeFinalStart(when) !== null
  && confirmMissing({ hasDay: !!when.date, hasClock: !!when.time, place }) === null;
